import { TimeRange } from '@fullstory/proto/fs/api/segment';
import { TrendFrequency } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  differenceInDays,
  differenceInHours,
} from 'date-fns';
import { isEqual } from 'lodash';
import { useCallback, useRef, useState } from 'react';

type ZoomItem = { timeRange: TimeRange; frequency: TrendFrequency };

/**
 * Hook to manage zooming in and out of a time series charts.
 *
 * We start have an internal stack of items that represent the zoom level.
 * When this is only one item, it is the original time range and frequency,
 * otherwise we treat this like a stack to handle the zoom out behaviour,
 * just popping the previous value off.
 *
 * The returned interval and frequency should be used with a compute call
 * to change the data displayed on the chart.
 *
 * Rolling windows are not currently supported.
 *
 * @param initialTimeRange the object's starting time range
 * @param initialFrequency the object's starting frequency

 */
export const useZoomOverlay = (initialTimeRange: TimeRange, initialFrequency: TrendFrequency) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const zoomStack = useRef<ZoomItem[]>([
    { timeRange: initialTimeRange, frequency: initialFrequency },
  ]);
  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [frequency, setFrequency] = useState(initialFrequency);
  const handleZoomIn = useCallback(
    (startDate: Date, endDate: Date) => {
      // since the end date is a range, we want to actually capture the end of the range
      const adjustedEndDate = addRangeToDate(endDate, frequency);
      const newFrequency = getZoomedFrequency(startDate, adjustedEndDate, initialFrequency);
      const timeRange = {
        startTime: startDate.toISOString(),
        endTime: adjustedEndDate.toISOString(),
      };

      // only push onto the zoom stack if the details are different
      const zoomItem: ZoomItem = { timeRange, frequency: newFrequency };
      if (!isEqual(zoomItem, zoomStack.current[zoomStack.current.length - 1])) {
        setTimeRange(timeRange);
        setFrequency(newFrequency);
        zoomStack.current.push(zoomItem);
        setIsZoomed(true);
      }
    },
    [frequency, initialFrequency],
  );

  const handleZoomOut = useCallback(() => {
    if (isZoomed) {
      zoomStack.current.pop();
      const previousDetails = zoomStack.current[zoomStack.current.length - 1];
      if (previousDetails) {
        setTimeRange(previousDetails.timeRange);
        setFrequency(previousDetails.frequency);
      }

      if (zoomStack.current.length === 1) {
        setIsZoomed(false);
      }
    }
  }, [isZoomed]);

  return {
    isZoomed,
    timeRange,
    frequency,
    handleZoomIn,
    handleZoomOut,
  };
};

const WEEK_THRESHOLD = 28;
const DAY_THRESHOLD = 7;
const HOUR_THRESHOLD = 6;

export function getZoomedFrequency(
  startDate: Date,
  endDate: Date,
  frequency: TrendFrequency,
): TrendFrequency {
  const days = differenceInDays(endDate, startDate);
  const hours = differenceInHours(endDate, startDate);
  let newFrequency = frequency;
  if (days >= WEEK_THRESHOLD && frequency !== TrendFrequency.TREND_FREQUENCY_DAILY) {
    newFrequency = TrendFrequency.TREND_FREQUENCY_WEEKLY;
  } else if (days >= DAY_THRESHOLD) {
    newFrequency = TrendFrequency.TREND_FREQUENCY_DAILY;
  } else if (days < DAY_THRESHOLD && hours >= HOUR_THRESHOLD) {
    newFrequency = TrendFrequency.TREND_FREQUENCY_HOURLY;
  } else if (hours < HOUR_THRESHOLD) {
    newFrequency = TrendFrequency.TREND_FREQUENCY_MINUTELY;
  }

  return newFrequency;
}

function addRangeToDate(date: Date, frequency: TrendFrequency): Date {
  const multiplier = 1;
  let adjustedEndDate = date;
  switch (frequency) {
    case TrendFrequency.TREND_FREQUENCY_MONTHLY:
      adjustedEndDate = addMonths(adjustedEndDate, multiplier);
      break;
    case TrendFrequency.TREND_FREQUENCY_WEEKLY:
      adjustedEndDate = addWeeks(adjustedEndDate, multiplier);
      break;
    case TrendFrequency.TREND_FREQUENCY_DAILY:
      adjustedEndDate = addDays(adjustedEndDate, multiplier);
      break;
    case TrendFrequency.TREND_FREQUENCY_HOURLY:
      adjustedEndDate = addHours(adjustedEndDate, multiplier);
      break;
    case TrendFrequency.TREND_FREQUENCY_MINUTELY:
      adjustedEndDate = addMinutes(adjustedEndDate, multiplier);
      break;
  }

  return adjustedEndDate;
}
